"use client";

import { useEffect, useState } from "react";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import type { ClinicAuthProof } from "@/components/clinic-auth-proof";
// This import deliberately points at the local copied prototype inside
// `src/components`, not the original sibling repo. We moved away from
// cross-project aliases because Turbopack would panic when CSS/Tailwind
// traversed outside the app root.
import PSRApp from "@/components/PSRApp";
import { fetchPracticeByClinicId } from "@/lib/api/fetch-practice";
import { useClaritySession } from "@/lib/auth/auth-store";

export function PracticeWorkspace() {
  const { tenantId } = useTenantRouteContext();
  const session = useClaritySession();
  const [clinicAuthProof, setClinicAuthProof] = useState<ClinicAuthProof>({
    status: "loading",
    clinicId: tenantId,
  });

  useEffect(() => {
    if (session.status !== "authenticated" || !session.accessToken) {
      return;
    }

    const accessToken = session.accessToken;
    let isCancelled = false;

    async function verifyClinicToken() {
      setClinicAuthProof({ status: "loading", clinicId: tenantId });

      try {
        const practice = await fetchPracticeByClinicId(tenantId, accessToken);

        if (isCancelled) {
          return;
        }

        setClinicAuthProof({
          status: "verified",
          clinicId: tenantId,
          practiceName:
            practice.displayName ??
            practice.legalName ??
            practice.practiceSlug ??
            practice.practiceId ??
            tenantId,
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setClinicAuthProof({
          status: "error",
          clinicId: tenantId,
          message:
            error instanceof Error
              ? error.message
              : "Unable to verify clinic access.",
          statusCode:
            typeof error === "object" &&
            error !== null &&
            "status" in error &&
            typeof error.status === "number"
              ? error.status
              : 500,
        });
      }
    }

    void verifyClinicToken();

    return () => {
      isCancelled = true;
    };
  }, [session.accessToken, session.status, tenantId]);

  return (
    <div className="workspace-theme relative h-screen overflow-hidden bg-(--surface-page) text-(--content-primary)">
      <PSRApp key={tenantId} clinicAuthProof={clinicAuthProof} />
    </div>
  );
}
